import { createElement, FC, useMemo } from 'react';
import classNames from 'classnames';

import styles from './CardBorderHeading.module.scss';

type BorderRadiusType = 'round' | 'square';
const isBorderRadiusSetting = (
  borderRadiusType: string | undefined
): borderRadiusType is BorderRadiusType =>
  typeof borderRadiusType === 'string' && ['round', 'square'].includes(borderRadiusType);

const sectionRootElementHeadings = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);
const CardBorderHeadingRootElement: FC<{ className?: string; isSection: boolean }> = ({
  children,
  className,
  isSection,
}) =>
  isSection ? (
    <section className={className}>{children}</section>
  ) : (
    <div className={className}>{children}</div>
  );

interface Props {
  borderRadiusType?: BorderRadiusType;
  children: React.ReactNode;
  heading?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';
  headingText: string;
}

const CardBorderHeading: FC<Props> = ({
  children,
  borderRadiusType = 'square',
  heading = 'p',
  headingText,
}) => {
  const borderRadiusSetting = isBorderRadiusSetting(borderRadiusType) ? borderRadiusType : 'round';
  const isSectionRootElement = useMemo(() => sectionRootElementHeadings.has(heading), [heading]);
  const Heading = useMemo(
    () =>
      createElement(heading, {
        children: headingText,
        className: styles.heading,
      }),
    [heading, headingText]
  );
  return (
    <CardBorderHeadingRootElement isSection={isSectionRootElement} className={styles.container}>
      {Heading}
      <div className={classNames(styles['overflow-hider'], styles[borderRadiusSetting])}>
        {children}
      </div>
    </CardBorderHeadingRootElement>
  );
};

export type { Props as CardBorderHeadingProps };
export default CardBorderHeading;
